import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DateTime } from "luxon";
import { IconButton, Typography } from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { MultiInputDateRangeField } from "@mui/x-date-pickers-pro/MultiInputDateRangeField";
import { useField } from "formik";
import ClearIcon from "@mui/icons-material/Clear";

import { TooltipLabel } from "../../Tooltips";
import { datePickerVariantSx, DatePickerVariant } from "./styles";

const SX_HOVER_INPUT = {
  "&:hover .MuiButtonBase-root.MuiIconButton-root": { opacity: 1 },
};

const SX_CLEAR_ICON = {
  order: 1,
  fontSize: "1em",
  padding: 0,
  opacity: 0,
  color: "text.secondary",
};

type FormikDateRangePickerProps = {
  startFieldName: string;
  endFieldName: string;
  startLabel?: string;
  endLabel?: string;
  startTooltip?: string;
  endTooltip?: string;
  disablePast?: boolean;
  lockPast?: boolean;
  showClearButtons?: boolean;
  showSeparator?: boolean;
  variant?: DatePickerVariant;
};

/**
 * Date range picker — Formik-aware.
 * Use inside a <Formik> context. Reads and writes two fields directly into
 * Formik state via useField(startFieldName / endFieldName).
 *
 * - `startFieldName` / `endFieldName`: Formik field names (e.g. "start", "stop")
 * - `startLabel` / `endLabel` / `*Tooltip`: labels and tooltips (i18n keys)
 * - `variant`: "filled" (default, no border) or "outlined" (with border, no background)
 * - `disablePast`: prevents selecting dates in the past
 * - `lockPast`: disables the start field when its value is already in the past
 * - `showClearButtons`: per-field × buttons (visible on hover)
 * - `showSeparator`: displays the "TO" separator between the two fields
 */
const FormikDateRangePicker = ({
  startFieldName,
  endFieldName,
  startLabel = "DATE_START_INCLUDED",
  endLabel = "DATE_END_INCLUDED",
  startTooltip,
  endTooltip,
  disablePast,
  lockPast,
  showClearButtons,
  showSeparator,
  variant = "filled",
}: FormikDateRangePickerProps) => {
  const intl = useIntl();
  const [startField, startMeta, startHelpers] = useField(startFieldName);
  const [endField, endMeta, endHelpers] = useField(endFieldName);

  const isStartLocked = !!(
    lockPast &&
    DateTime.isDateTime(startField.value) &&
    startField.value.startOf("day") < DateTime.now().startOf("day")
  );

  const resolveLabel = (labelKey: string, tooltip?: string) => {
    if (tooltip) {
      return (
        <TooltipLabel
          label={intl.formatMessage({ id: labelKey })}
          tooltipLabel={intl.formatMessage({ id: tooltip })}
          translateTooltip={false}
        />
      );
    }
    return <FormattedMessage id={labelKey} />;
  };

  const buildTextFieldProps = (
    meta: { touched: boolean; error?: string },
    helpers: { setValue: (value: DateTime | null) => void },
    labelKey: string,
    tooltip: string | undefined,
    disabled?: boolean
  ) => ({
    sx: [
      datePickerVariantSx[variant],
      { minWidth: 0, ...(showClearButtons ? SX_HOVER_INPUT : {}) },
    ],
    error: !!(meta.touched && meta.error),
    helperText: meta.touched ? meta.error : undefined,
    disabled,
    label: resolveLabel(labelKey, tooltip),
    variant: "outlined" as const,
    ...(showClearButtons && !disabled
      ? {
          slotProps: {
            input: {
              endAdornment: (
                <IconButton
                  onClick={() => helpers.setValue(null)}
                  aria-label={intl.formatMessage({ id: "RESET" })}
                  sx={SX_CLEAR_ICON}
                >
                  <ClearIcon sx={{ fontSize: "1.25em" }} />
                </IconButton>
              ),
            },
          },
        }
      : {}),
  });

  return (
    <DateRangePicker
      disablePast={disablePast}
      onChange={([start, stop]) => {
        startHelpers.setValue(start ?? null);
        endHelpers.setValue(stop ?? null);
      }}
      slots={{ field: MultiInputDateRangeField }}
      slotProps={{
        field: {
          sx: {
            alignItems: "center",
            width: "100%",
            mt: showSeparator ? 3 : 1,
            mb: showSeparator ? 2 : 0.5,
          },
          slots: {
            separator: showSeparator
              ? () => (
                  <Typography>
                    <FormattedMessage id="TO" />
                  </Typography>
                )
              : () => null,
          },
          slotProps: {
            textField: ({ position }: { position: "start" | "end" }) =>
              position === "start"
                ? buildTextFieldProps(
                    startMeta,
                    startHelpers,
                    startLabel,
                    startTooltip,
                    isStartLocked
                  )
                : buildTextFieldProps(
                    endMeta,
                    endHelpers,
                    endLabel,
                    endTooltip
                  ),
          },
        } as any,
      }}
      value={[startField.value, endField.value]}
    />
  );
};

export default FormikDateRangePicker;
